import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { http, formatApiErrorDetail } from "@/lib/api";

const AuthContext = createContext(null);

// Defer setState to escape React Compiler's render-time tracking.
const defer = (fn) => queueMicrotask(fn);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    const token = localStorage.getItem("sjds_token");
    if (!token) { setUser(false); return; }
    http.get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => setUser(false));
  }, []);

  const login = useCallback(async (email, password) => {
    try {
      const { data } = await http.post("/auth/login", { email, password });
      if (data.access_token) localStorage.setItem("sjds_token", data.access_token);
      defer(() => { setUser(data); setError(""); });
      return true;
    } catch (e) {
      defer(() => setError(formatApiErrorDetail(e?.response?.data?.detail) || e.message));
      return false;
    }
  }, []);

  const register = useCallback(async (email, password, name) => {
    try {
      const { data } = await http.post("/auth/register", { email, password, name });
      if (data.access_token) localStorage.setItem("sjds_token", data.access_token);
      defer(() => { setUser(data); setError(""); });
      return true;
    } catch (e) {
      defer(() => setError(formatApiErrorDetail(e?.response?.data?.detail) || e.message));
      return false;
    }
  }, []);

  const logout = useCallback(async () => {
    try { await http.post("/auth/logout"); } catch (_e) { /* ignore */ }
    localStorage.removeItem("sjds_token");
    defer(() => setUser(false));
  }, []);

  const refresh = useCallback(async () => {
    try {
      const { data } = await http.get("/auth/me");
      defer(() => setUser(data));
    } catch (_e) {
      defer(() => setUser(false));
    }
  }, []);

  return (
    <AuthContext.Provider value={{ user, error, login, register, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
